import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronRight,
  faRotateLeft,
  faSpinner,
  faCodeBranch,
  faLink,
  faLinkSlash,
  faFileExport,
  faCodeMerge,
} from "@fortawesome/free-solid-svg-icons"
import { Tooltip } from "antd"

import { AuditRecord } from "types/questionnaire.types"
import { HashChip } from "shared/ui/HashChip/HashChip.component"
import { parseServerDate } from "utils/serverDate"
import { formatRelativeTime } from "utils/relativeTime"
import { ACCENT } from "constants/brand"
import { HistoryRecordDetails } from "./HistoryRecordDetails.component"
import { mergeSummary } from "./DataRecordDetails.component"
import { collapseRemaps, isDummyImport, isVersionRecord, recordTitle } from "../utils/historyLabels"

// When the entry was made, in words, with the exact date and time on hover (from a week back the
// words are only the date). A time the client can't read is shown as it came.
const EntryTime: React.FC<{ performedAt: string }> = ({ performedAt }) => {
  const date = parseServerDate(performedAt)
  if (!date) return <>{performedAt}</>
  const exact = date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
  return (
    <Tooltip title={exact}>
      <span>{formatRelativeTime(date)}</span>
    </Tooltip>
  )
}

// The one-line summary under an entry's title
const changeSummary = (rec: AuditRecord): string => {
  if (rec.memberProjectId && rec.action === "wave.linked") return "A wave joined this tracker"
  if (rec.memberProjectId && rec.action === "wave.unlinked") return "A wave left this tracker"
  if (rec.action === "data.merged") return mergeSummary(rec)
  if (rec.action === "wave.forked") return "Forked from a previous wave: start of this wave's history"
  if (rec.action === "wave.linked") return "Linked to another project as part of a tracker"
  if (rec.action === "wave.unlinked") return "Left its tracker"
  if (rec.action === "deliverable.exported")
    return `Exported ${rec.filename || "a deliverable"}${rec.format ? ` (${rec.format})` : ""}`
  // As the rows show them: no code changes beside an added, removed or moved answer
  const n = collapseRemaps(rec.changes ?? []).length
  if (rec.action === "data.imported") {
    const imported = isDummyImport(rec) ? "Dummy data imported" : "Data imported"
    if (rec.parseStatus === "parse_failed") return `${imported}: couldn't compare against the questionnaire`
    // Map-less imports (JSON / direct DQD) and projects without a questionnaire can't claim to
    // match: the record pins the contents, it doesn't compare anything.
    if (rec.parseStatus === "no_qnr") return `${imported}: no questionnaire to compare against`
    if (rec.parseStatus === "no_map") return `${imported}: contents recorded (no datamap to compare)`
    return n === 0
      ? `${imported}: matches the questionnaire`
      : `${imported}: ${n} difference${n === 1 ? "" : "s"} from the questionnaire`
  }
  if (rec.parseStatus === "parse_failed") return "Couldn't read the questions, so the changes aren't listed"
  if (n === 0) return "No metadata changes"
  return `${n} metadata change${n === 1 ? "" : "s"}`
}

interface HistoryRecordItemProps {
  rec: AuditRecord
  isLatest: boolean
  isCurrentVersion: boolean
  versionNumber?: number
  isBroken: boolean
  expanded: boolean
  onToggle: () => void
  onRestore?: () => void
  restoreHint?: string
  /** This entry's restore is running (shows the spinner) */
  restoring: boolean
  /** A restore is running, of this entry or another: every Restore button waits for it */
  restoreDisabled?: boolean
  onJumpToQuestion?: (label: string) => void
  onViewLineage?: (hash: string) => void
  /** Switch to the ancestry view (Provenance tab only, omitted elsewhere). */
  onViewAncestry?: () => void
  onOpenInEditor?: () => void
}

/** One entry of the project history: a version, an import, an export or a wave event. */
export const HistoryRecordItem: React.FC<HistoryRecordItemProps> = ({
  rec,
  isLatest,
  isCurrentVersion,
  versionNumber,
  isBroken,
  expanded,
  onToggle,
  onRestore,
  restoreHint,
  restoring,
  restoreDisabled = false,
  onJumpToQuestion,
  onViewLineage,
  onViewAncestry,
  onOpenInEditor,
}) => {
  const initials = (rec.performedByName || rec.performedBy || "?")
    .split(/[\s_]+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()

  return (
    <li className="relative pl-8" id={`vh-rec-${rec.seq}`}>
      {/* timeline rail */}
      <span className="absolute left-[11px] top-0 h-full w-px bg-slate-200" aria-hidden />
      <span
        className={`absolute left-[6px] top-3 h-2.5 w-2.5 rounded-full ring-4 ring-white ${
          isBroken ? "bg-rose-500" : isLatest ? "" : "bg-slate-300"
        }`}
        style={isLatest && !isBroken ? { backgroundColor: ACCENT } : undefined}
        aria-hidden
      />

      <div
        className={`mb-2 rounded-lg border bg-white transition-shadow ${
          expanded ? "border-slate-300 shadow-sm" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left"
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            className={`mt-1 h-3 w-3 flex-none text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
          <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-slate-800">{recordTitle(rec, versionNumber)}</span>
              {isCurrentVersion && (
                <span
                  className="rounded-full px-1.5 py-px text-[10px] font-semibold text-white"
                  style={{ backgroundColor: ACCENT }}
                >
                  current
                </span>
              )}
              {rec.parseStatus === "parse_failed" && (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700">
                  couldn&apos;t read
                </span>
              )}
              {rec.action === "wave.forked" && (
                <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700">
                  <FontAwesomeIcon icon={faCodeBranch} className="h-2.5 w-2.5" />
                  forked
                </span>
              )}
              {rec.action === "wave.linked" && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700">
                  <FontAwesomeIcon icon={faLink} className="h-2.5 w-2.5" />
                  linked
                </span>
              )}
              {rec.action === "wave.unlinked" && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700">
                  <FontAwesomeIcon icon={faLinkSlash} className="h-2.5 w-2.5" />
                  left
                </span>
              )}
              {rec.action === "deliverable.exported" && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-700">
                  <FontAwesomeIcon icon={faFileExport} className="h-2.5 w-2.5" />
                  exported
                </span>
              )}
              {rec.action === "data.merged" && (
                <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-px text-[10px] font-medium text-indigo-700">
                  <FontAwesomeIcon icon={faCodeMerge} className="h-2.5 w-2.5" />
                  merged
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-[12px] text-slate-500">
              {rec.performedByName || rec.performedBy} · <EntryTime performedAt={rec.performedAt} />
            </div>
            <div className="mt-0.5 text-[12px] text-slate-600">{changeSummary(rec)}</div>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-slate-100 px-3 pb-3 pt-2">
            <HistoryRecordDetails
              rec={rec}
              onJumpToQuestion={onJumpToQuestion}
              onViewLineage={onViewLineage}
              onViewAncestry={onViewAncestry}
            />

            <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
              <HashChip hash={rec.recordHash} label="#" length={10} />
              {onRestore && !isCurrentVersion && isVersionRecord(rec.action) && !!rec.rawHash ? (
                <button
                  type="button"
                  onClick={onRestore}
                  disabled={restoring || restoreDisabled}
                  className="dq-btn"
                >
                  <FontAwesomeIcon icon={restoring ? faSpinner : faRotateLeft} className={`h-3 w-3 ${restoring ? "animate-spin" : ""}`} />
                  Restore this version
                </button>
              ) : onOpenInEditor && isVersionRecord(rec.action) ? (
                // Restoring changes the questionnaire, so it is done in the editor. From the
                // Provenance timeline we deep-link instead.
                <button
                  type="button"
                  onClick={onOpenInEditor}
                  className="dq-btn"
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
                  Open in editor
                </button>
              ) : restoreHint && !isCurrentVersion && isVersionRecord(rec.action) ? (
                <span className="text-[12px] text-slate-400">{restoreHint}</span>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </li>
  )
}

export default HistoryRecordItem
